<template>
  <div class="event-tickets-container pb-10">
    <v-tabs
      v-model="selectedTab"
      background-color="transparent"
      color="var(--color-dark-800)"
      class="tabs"
      grow
    >
      <v-tab
        v-for="tab in tabs"
        :key="tab"
        :tab-value="tab"
      >
        {{ tab === 'admission' ? 'Admission' : 'Promotions' }}
      </v-tab>
    </v-tabs>

    <v-tabs-items
      v-model="selectedTab"
      class="tabs-items"
    >
      <v-tab-item value="admission">
        <div class="tiers-list">
          <div
            v-for="tier in ticketTiers"
            :key="tier.id"
            class="tier-item"
          >
            <p class="tier-title">{{ tier.tierTitle }}</p>
            <p class="tier-price">{{ centsToDollars(tier.price) }}</p>
            <p class="tier-description">
              Limit: {{ tier.limit === 0 ? 'Unlimited' : tier.limit
              }}{{ tier.description ? `, ${tier.description}` : '' }}
            </p>
            <v-btn
              icon
              dense
              class="edit-button"
              @click="editTicketTier(tier)"
            >
              <v-icon color="var(--color-blue-800)">{{ mdiPencilOutline }}</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="tab-item-bottom">
          <BKButton
            class="add-button"
            text
            width="auto"
            height="40"
            @click="addTicketTier"
          >
            Add a ticket <v-icon>{{ mdiPlus }}</v-icon>
          </BKButton>
          <p class="bottom-text">
            Create a new ticket to start selling admission to your event. You can also connect tickets to battles
          </p>
        </div>
        <EditTicketTierModal
          :event-id="eventData.id"
          :is-open="ticketTierModal.isOpen"
          :ticket-tier="ticketTierModal.ticketTier"
          :total-battles="eventData.battles?.length || 0"
          @update:ticket-tier="closeTicketTierModal"
          @close="closeTicketTierModal"
        />
      </v-tab-item>
      <v-tab-item value="promotions">
        <div class="discounts-list">
          <div
            v-for="discount in discounts"
            :key="discount.id"
            class="discount-item"
          >
            <p class="discount-title">{{ discount.type === 'code' ? discount.code : discount.title }}</p>
            <p class="discount-amount">
              -{{ discount.isPercentage ? `${discount.amount}%` : centsToDollars(discount.amount) }}
            </p>
            <div class="discount-description">
              <div class="discount-info-row">
                <span class="discount-type">
                  {{ discount.type === 'code' ? 'Discount code' : 'Early bird pricing' }}
                </span>
                <span class="discount-purchase-limit">
                  {{ discount.ticketLimits === -1 ? '• No limit' : `• ${discount.ticketLimits} tickets limit` }}
                </span>
                <span class="discount-dates">
                  • {{ new Date(discount.validFrom).toLocaleDateString('en-US', { dateStyle: 'short' }) }} -
                  {{ new Date(discount.validTo).toLocaleDateString('en-US', { dateStyle: 'short' }) }}
                </span>
              </div>
              <div class="discount-info-row">Applicable to: {{ getApplicableTicketTitles(discount) }}</div>
            </div>
            <v-btn
              icon
              dense
              class="edit-button"
              @click="editDiscount(discount)"
            >
              <v-icon color="var(--color-blue-800)">{{ mdiPencilOutline }}</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="discount-buttons">
          <button
            class="discount-button"
            @click="addDiscountCode"
          >
            <span class="button-title">
              Add discount code <v-icon color="var(--color-blue-800)">{{ mdiPlus }}</v-icon>
            </span>
            <span class="button-subtitle"> To all or specific tickets </span>
          </button>
          <button
            class="discount-button"
            @click="addEarlyBirdDiscount"
          >
            <span class="button-title">
              Add early bird pricing <v-icon color="var(--color-blue-800)">{{ mdiPlus }}</v-icon>
            </span>
            <span class="button-subtitle"> To all or specific tickets </span>
          </button>
        </div>
        <EditTicketDiscountModal
          v-if="discountModal.isOpen"
          :event-id="eventData.id"
          :is-open="discountModal.isOpen"
          :discount="discountModal.discount"
          :type="discountModal.discountType"
          :ticket-tiers="ticketTiers"
          :event-start-time="eventData.dateStart"
          @update:discount="closeDiscountModal"
          @close="closeDiscountModal"
        />
      </v-tab-item>
    </v-tabs-items>

    <EventEditActionButtons
      :is-loading="false"
      :disabled="false"
      :error="errorMessage"
      @next="handleNextButton"
      @back="handleBackButton"
      @cancel="emit('cancel')"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { mdiPencilOutline, mdiPlus } from '@mdi/js'

import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { IEventEdit } from '@/enitites/event'
import { IEventTicketTier, IEventDiscountType, IEventDiscount } from '@/enitites/event/model/types'
import EditTicketTierModal from '@/pages/CreateEventProcessingPage/ui/EditTicketTierModal.vue'
import EditTicketDiscountModal from '@/pages/CreateEventProcessingPage/ui/EditTicketDiscountModal.vue'
import { centsToDollars } from '@/services/currency'

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const errorMessage = ref<string>()
const tabs = ['admission', 'promotions']
const selectedTab = ref('admission')
const ticketTierModal = reactive<{
  isOpen: boolean
  ticketTier: null | IEventTicketTier
}>({
  isOpen: false,
  ticketTier: null,
})
const discountModal = reactive<{
  isOpen: boolean
  discount: IEventDiscount | null
  discountType: IEventDiscountType | null
}>({
  isOpen: false,
  discount: null,
  discountType: 'code',
})

const ticketTiers = computed<IEventTicketTier[]>(() => {
  return [...(props.eventData.tiers || [])].sort((a, b) => a.id - b.id)
})
const discounts = computed<IEventDiscount[]>(() => {
  return [...(props.eventData.discounts || [])].sort((a, b) => a.id - b.id)
})

function getApplicableTicketTitles(discount: IEventDiscount) {
  if (!discount.applicableTicketTiers || discount.applicableTicketTiers.length === 0) {
    return 'All tickets'
  }
  return discount.applicableTicketTiers
    .map((tId) => {
      const tier = ticketTiers.value.find((tt) => tt.id === tId)
      return tier ? tier.tierTitle : 'Unknown tier'
    })
    .join(', ')
}

function closeTicketTierModal() {
  ticketTierModal.isOpen = false
  ticketTierModal.ticketTier = null
}

function addTicketTier() {
  ticketTierModal.isOpen = true
  ticketTierModal.ticketTier = null
}

function editTicketTier(tier: IEventTicketTier) {
  ticketTierModal.isOpen = true
  ticketTierModal.ticketTier = tier
}

function closeDiscountModal() {
  discountModal.isOpen = false
  discountModal.discount = null
  discountModal.discountType = null
}

function addDiscountCode() {
  discountModal.discountType = 'code'
  discountModal.isOpen = true
  discountModal.discount = null
}

function addEarlyBirdDiscount() {
  discountModal.discountType = 'early_bird'
  discountModal.isOpen = true
  discountModal.discount = null
}

function editDiscount(discount: IEventDiscount) {
  discountModal.isOpen = true
  discountModal.discount = discount
  discountModal.discountType = discount.type
}

const handleNextButton = () => {
  if (selectedTab.value === 'admission') {
    selectedTab.value = 'promotions'
  } else {
    emit('next')
  }
}

const handleBackButton = () => {
  if (selectedTab.value === 'promotions') {
    selectedTab.value = 'admission'
  } else {
    emit('back')
  }
}

onMounted(() => {
  if (props.eventData.tiers?.length === 0) {
    addTicketTier()
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.event-tickets-container {
  max-width: 650px;
  margin: 40px auto 60px;

  .v-tab {
    text-transform: unset;
    font-size: 16px;
    line-height: 24px;

    &.v-tab--active {
      font-weight: 700;
    }
  }

  .tabs {
    margin-bottom: 40px;
  }

  .tabs-items {
    background-color: transparent;
    overflow: visible;
  }

  .tab-item-bottom {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 8px;

    .bottom-text {
      font-size: 14px;
      line-height: 20px;
      color: var(--color-grey-300);
    }
  }

  .tiers-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;

    .tier-item {
      display: grid;
      grid-template-areas:
        'title price edit'
        'description description description';
      grid-template-columns: 1fr auto auto;
      align-items: center;
      gap: 8px 16px;
      padding: 20px;
      background-color: var(--color-white);
      box-shadow: 0 4px 12px 0 rgb(0 0 0 / 12%);
      border-radius: 6px;

      p {
        margin: 0;
      }

      .tier-title {
        grid-area: title;
        font-weight: 500;
        font-size: 20px;
        line-height: 26px;
        color: var(--color-dark-800);
      }

      .tier-price {
        grid-area: price;
        font-weight: 500;
        font-size: 20px;
        line-height: 26px;
        color: var(--color-dark-800);
      }

      .edit-button {
        grid-area: edit;
      }

      .tier-description {
        grid-area: description;
        font-size: 14px;
        line-height: 20px;
        color: var(--color-grey-300);
      }
    }
  }

  .add-button {
    padding: 0;
  }

  .discounts-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;

    .discount-item {
      display: grid;
      grid-template-areas:
        'title amount edit'
        'description description description';
      grid-template-columns: 1fr auto auto;
      align-items: center;
      gap: 8px 16px;
      padding: 20px;
      background-color: var(--color-white);
      box-shadow: 0 4px 12px 0 rgb(0 0 0 / 12%);
      border-radius: 6px;

      p {
        margin: 0;
      }

      .discount-title {
        grid-area: title;
        font-weight: 500;
        font-size: 20px;
        line-height: 26px;
        color: var(--color-dark-800);
      }

      .discount-amount {
        grid-area: amount;
        font-weight: 500;
        font-size: 20px;
        line-height: 26px;
        color: var(--color-dark-800);
      }

      .edit-button {
        grid-area: edit;
      }

      .discount-description {
        grid-area: description;
        font-size: 14px;
        line-height: 20px;
        color: var(--color-grey-300);

        .discount-info-row {
          margin-bottom: 4px;

          .discount-type {
            font-weight: 700;
          }
        }
      }
    }
  }

  .discount-buttons {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;

    .discount-button {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      padding: 8px;
      border: 1px solid var(--color-blue-800);
      border-radius: 6px;
      cursor: pointer;
      background-color: transparent;
      transition: background-color 0.1s ease-in-out;

      &:hover,
      &:focus-visible {
        background-color: rgb(4 30 66 / 10%);
      }

      &:active {
        background-color: rgb(4 30 66 / 15%);
      }

      .button-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--color-blue-800);
        font-family: ff-good-web-pro, Roboto, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 16px;
        line-height: 24px;
        text-transform: uppercase;
      }

      .button-subtitle {
        font-size: 14px;
        line-height: 20px;
        color: var(--color-grey-300);
      }
    }
  }
}
</style>
